import { type Tag } from '../hooks/useTags';

export const OUTPUT_TYPE = {
  Pdf: 'PDF',
  Tiff: 'TIFF',
  Text: 'Text',
  NativePdf: 'Native PDF',
  Native: 'Native',
  NativeWithPlaceholder: 'Native with Placeholder',
} as const;

export const COLOR_SETTING = {
  BlackAndWhite: 'Black & White',
  Color: 'Color',
  ColorTaggedOnly: 'Color Tagged Only',
} as const;

export const ENDORSEMENT_TYPE = {
  None: 'NONE',
  Bates: 'BATES',
  Text: 'TEXT',
  Tag: 'TAG',
} as const;
export type EndorsementTypeValue = typeof ENDORSEMENT_TYPE[keyof typeof ENDORSEMENT_TYPE];

export interface EndorsementItem {
  id: string;
  name: string;
  type: EndorsementTypeValue;
}

export interface EndorsementTypes {
  west: EndorsementTypeValue;
  south: EndorsementTypeValue;
  east: EndorsementTypeValue;
}

export interface ExportFormState {
  id: string;
  projectId: string;
  name: string;
  startValue: number;
  prefix: string;
  delimiter: string;
  filesPerDirectory: number;
  collectionIds: string[];
  tags: { and: { id: string; name: string }[]; or: { id: string; name: string }[]; not: { id: string; name: string }[] };
  loadFileFormatIds: string[];
  relativitySettingsId: string;
  zeroPaddingLength: number;
  colorSetting: string;
  tiffExportColorFileFormat: string;
  // Full tag object; mapped to the backend's `colorTagId` (a TagResponse, not an id) on submit.
  colorTag: Tag | null;
  outputCustomNativeFilename: boolean;
  outputOriginalNativePaths: boolean;
  spreadsheetPlaceholder: boolean;
  presentationPlaceholder: boolean;
  southWestEndorsement: EndorsementItem[];
  southEndorsement: EndorsementItem[];
  southEastEndorsement: EndorsementItem[];
  outputTypes: string[];
  tagPlaceholderEnabled: boolean;
  tagPlaceholder: { id: string; name: string } | null;
  containerExportTag: { id: string; name: string } | null;
  includeContainerExportTag: boolean;
  virusPlaceholderText: string;
  failedToImagePlaceholderText: string;
  nativeFormatPlaceholderText: string;
  includeDuplicateResources: boolean;
  includeRedactedResources: boolean;
  includeResourcesWithProcessingExceptions: boolean;
  includeResourcesWithZeroPages: boolean;
  keepPageOrientation: boolean;
  collectionNames: string[];
  loadFileFormatNames: string[];
  directToRelativity: boolean;
}

export function initialFormState(projectId: string): ExportFormState {
  return {
    id: '',
    projectId,
    name: '',
    startValue: 1,
    prefix: 'iio',
    delimiter: '-',
    filesPerDirectory: 1000,
    collectionIds: [],
    tags: { and: [], or: [], not: [] } as { and: { id: string; name: string }[]; or: { id: string; name: string }[]; not: { id: string; name: string }[] },
    loadFileFormatIds: [],
    relativitySettingsId: '',
    zeroPaddingLength: 6,
    colorSetting: COLOR_SETTING.BlackAndWhite,
    tiffExportColorFileFormat: 'TIF',
    colorTag: null,
    outputCustomNativeFilename: false,
    outputOriginalNativePaths: false,
    spreadsheetPlaceholder: false,
    presentationPlaceholder: false,
    southWestEndorsement: [],
    southEndorsement: [],
    southEastEndorsement: [{ id: 'Bates', name: 'Bates', type: ENDORSEMENT_TYPE.Bates }],
    outputTypes: [],
    tagPlaceholderEnabled: false,
    tagPlaceholder: null,
    containerExportTag: null,
    includeContainerExportTag: false,
    virusPlaceholderText: 'Not produced due to virus',
    failedToImagePlaceholderText: 'Produced in native format',
    nativeFormatPlaceholderText: 'Produced in native format',
    includeDuplicateResources: true,
    includeRedactedResources: true,
    includeResourcesWithProcessingExceptions: true,
    includeResourcesWithZeroPages: true,
    keepPageOrientation: false,
    collectionNames: [],
    loadFileFormatNames: [],
    directToRelativity: false,
  };
}

export interface LoadFileField {
  name: string;
  outputName: string;
  type: 'normal' | 'custom' | 'tag';
  isEnabled: boolean;
  parts: Array<{ id: string; name: string }>;
}

export interface LoadFileFormat {
  id?: string;
  name: string;
  type: 'system' | 'user';
  subType: string;
  fileExtension: string;
  fields: LoadFileField[];
  encoding: string;
  hasHeaderRow: boolean;
  dateFormat: string;
  timeFormat: string;
  dateTimeFormat: string;
  prependPath: string | null;
  pathSeparator: string;
  columnDelimiterCharCode: number;
  quoteCharCode: number;
  multiValueCharCode: number;
  nestedValuesCharCode: number;
  embeddedNewlineCharCode: number;
  includeMultiValueFieldConfig: boolean;
  newline: { type: 'windows' | 'unix' };
  template?: string;
  projectId?: string;
}
